import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { DEFAULT_COUNTRY } from '@src/config/localisation/locale-config.constants'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { Button, BUTTON_TYPES } from '@src/quince-components/src'
import { isValidZipCodeForLocale } from '@src/quince-site-common/src/hooks/zipcode/utils/zipcode.utils'
import { isValidEmail } from '@src/quince-site-integrated/src/utils'
import { parseLocale } from '@src/utils/i18n.utils'

import { FormField } from '../utils'

import styles from './searchOptions.module.scss'

interface Props {
  fields: FormField[]
  loading: boolean
  setLoading: (loading: boolean) => void
  setStep: (step: number) => void
  step: number
  handleSubmit: (values: Record<number, string>) => Promise<void>
  errorMessage: string
  setErrorMessage: (errorMessage: string) => void
  resetSearchError: () => void
}
export default function SearchBox(props: Props) {
  const { fields, loading, handleSubmit, setErrorMessage, resetSearchError } = props
  const { t } = useTranslation('common')
  const [formValues, setFormValues] = useState<Record<number, string>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({})

  const { locale } = useLocaleContext()
  const { country } = parseLocale(locale)
  const localeConfig = useStatsigDynamicLocaleConfig()

  const handleInputChange = (index: number, value: string) => {
    setErrorMessage('')
    resetSearchError?.()
    // Clear field-specific error
    if (fieldErrors[index]) {
      setFieldErrors(prev => {
        const newErrors: Record<number, string> = {}
        Object.entries(prev).forEach(([key, val]) => {
          if (Number(key) !== index) {
            newErrors[Number(key)] = val
          }
        })
        return newErrors
      })
    }
    setFormValues(prev => ({
      ...prev,
      [index]: value,
    }))
  }

  const getFieldName = (field: FormField): string => {
    const fieldNameMap: Record<string, string> = {
      orderNumber: t('order_number'),
      zipCode: t('enter_shipping_code'),
      email: t('email_address_v1'),
      cardNumber: t('card_number_1'),
    }
    const mappedName = fieldNameMap[field.id]
    if (mappedName) {
      return mappedName.toLowerCase()
    }
    return field.id
  }

  const validateFields = (fieldsToValidate: FormField[], values: Record<number, string>) => {
    const errors: Record<number, string> = {}
    let hasErrors = false

    fieldsToValidate.forEach((field, index) => {
      const value = values[index]?.trim() ?? ''

      // Check if field is empty
      if (!value) {
        const fieldName = getFieldName(field)
        errors[index] = `${t('please_enter')} ${fieldName}`
        hasErrors = true
        return
      }

      // Validate card number is exactly 4 digits
      if (field.id === 'cardNumber') {
        if (!/^\d{4}$/.test(value)) {
          const fieldName = getFieldName(field)
          errors[index] = `${t('please_enter')} ${t('4_digit')} ${fieldName}`
          hasErrors = true
        }
      }

      // Validate email format if field is email type
      if (field.id === 'email' || field.type === 'email') {
        if (!isValidEmail(value.trim())) {
          errors[index] = t('please_enter_valid_email_address')
          hasErrors = true
        }
      }
      if (field.id === 'zipCode') {
        if (!isValidZipCodeForLocale(value.trim(), locale, localeConfig)) {
          errors[index] = t('please_enter_valid_code', {
            type: country.toLowerCase() === DEFAULT_COUNTRY ? t('zip_code') : t('postal_code'),
          })
          hasErrors = true
        }
      }
    })

    return { errors, hasErrors }
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    const { errors, hasErrors } = validateFields(fields, formValues)

    setFieldErrors(errors)

    if (hasErrors) {
      return
    }

    void handleSubmit(formValues)
  }

  return (
    <>
      <form onSubmit={handleFormSubmit}>
        {fields.map((field, index) => {
          return (
            <div key={index} className={styles.optionContainer}>
              <input
                type={field.type ?? 'text'}
                id={`field-${index}`}
                placeholder=" "
                value={formValues[index] ?? ''}
                onChange={e => {
                  if (field?.id === 'cardNumber') {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                  }
                  if (field?.id === 'orderNumber') {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '')
                  }
                  handleInputChange(index, e.target.value)
                }}
              />
              <label htmlFor={`field-${index}`}>{t(field.labelKey)}</label>
              {fieldErrors[index] && <div className={styles.errorMessage}>{fieldErrors[index]}</div>}
            </div>
          )
        })}

        <Button
          onClick={handleFormSubmit}
          showLoader={loading}
          type={BUTTON_TYPES.SECONDARY}
          className={styles.submitButton}
        >
          {t('search')}
        </Button>
      </form>
    </>
  )
}
